import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import * as Tabs from '@radix-ui/react-tabs';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CollapsibleChevron from '@/components/shared/CollapsibleChevron';
import HelpDocContent from '@/components/help/HelpDocContent';
import HighlightedText from '@/components/help/HighlightedText';
import Search from '@/icons/Search';
import Close from '@/icons/Close';
import type { DocChapter } from '@/types/help-docs';
import {
  filterDocChapters,
  findChapterById,
  firstMatchingSubsectionId,
  getUserDocChapters,
  resolveActiveChapterId
} from '@/utils/helpDocs';
import { getFaqsFromI18n, getUserDocsFromI18n, type FAQItem } from '@/utils/helpI18n';
import './help.css';

declare const __APP_VERSION__: string;

type TabId = 'faqs' | 'docs' | 'chat' | 'contact';

const VALID_TABS: TabId[] = ['faqs', 'docs', 'chat', 'contact'];

const HelpScreen: React.FC = () => {
  const { t, i18n } = useTranslation('help');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const tabParam = searchParams.get('tab') as TabId | null;
  const currentTab: TabId = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'faqs';

  const [activeTab, setActiveTab] = useState<TabId>(currentTab);
  const [expandedFaq, setExpandedFaq] = useState<string | undefined>(undefined);
  const [faqSearch, setFaqSearch] = useState('');
  const [docSearch, setDocSearch] = useState('');

  const version = __APP_VERSION__;
  const hash = location.hash.slice(1);
  const chapterParam = searchParams.get('chapter');
  const lastScrolledSearchMatchRef = useRef<string | null>(null);

  const docChapters = useMemo(
    () => getUserDocChapters(getUserDocsFromI18n()),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload docs when locale bundle changes
    [i18n.language]
  );

  const trimmedDocSearch = docSearch.trim();
  const highlightQuery = trimmedDocSearch || undefined;
  const filteredDocChapters = useMemo(
    () => filterDocChapters(docChapters, trimmedDocSearch),
    [docChapters, trimmedDocSearch]
  );

  const resolvedChapterId = useMemo(
    () => resolveActiveChapterId(docChapters, chapterParam, hash),
    [docChapters, chapterParam, hash]
  );

  const [activeChapterId, setActiveChapterId] = useState(resolvedChapterId);
  const [targetSubsectionId, setTargetSubsectionId] = useState<string | null>(null);

  const activeChapter = findChapterById(filteredDocChapters, activeChapterId) ?? filteredDocChapters[0] ?? null;

  useEffect(() => {
    if (currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
  }, [currentTab, activeTab]);

  useEffect(() => {
    if (!tabParam || !VALID_TABS.includes(tabParam)) {
      setSearchParams({ tab: 'faqs' }, { replace: true });
    }
  }, [tabParam, setSearchParams]);

  useEffect(() => {
    if (activeTab !== 'docs') {
      return;
    }

    if (trimmedDocSearch) {
      if (filteredDocChapters.length === 0) {
        return;
      }
      const stillVisible = filteredDocChapters.some((chapter) => chapter.id === activeChapterId);
      if (!stillVisible) {
        const nextChapter = filteredDocChapters[0];
        setActiveChapterId(nextChapter.id);
        navigate(`/help?tab=docs&chapter=${nextChapter.id}`, { replace: true });
      }
      return;
    }

    if (resolvedChapterId && resolvedChapterId !== activeChapterId) {
      setActiveChapterId(resolvedChapterId);
    }
  }, [activeTab, trimmedDocSearch, filteredDocChapters, activeChapterId, resolvedChapterId, navigate]);

  useEffect(() => {
    if (activeTab !== 'docs' || !hash || trimmedDocSearch) {
      if (!hash) {
        setTargetSubsectionId(null);
      }
      return undefined;
    }

    const subsectionExists = activeChapter?.subsections.some((subsection) => subsection.id === hash);
    if (!subsectionExists) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTargetSubsectionId(hash);
      }
    }, 50);

    return () => window.clearTimeout(timer);
  }, [activeTab, hash, activeChapterId, activeChapter, trimmedDocSearch]);

  useEffect(() => {
    if (activeTab !== 'docs' || !trimmedDocSearch || !activeChapter) {
      lastScrolledSearchMatchRef.current = null;
      return undefined;
    }

    const matchId = firstMatchingSubsectionId(activeChapter, trimmedDocSearch);
    if (!matchId) {
      return undefined;
    }

    const scrollKey = `${trimmedDocSearch}::${activeChapter.id}::${matchId}`;
    if (lastScrolledSearchMatchRef.current === scrollKey) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const el = document.getElementById(matchId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTargetSubsectionId(matchId);
        lastScrolledSearchMatchRef.current = scrollKey;
      }
    }, 50);

    return () => window.clearTimeout(timer);
  }, [activeTab, trimmedDocSearch, activeChapter]);

  useEffect(() => {
    if (!targetSubsectionId) {
      return undefined;
    }

    const timer = window.setTimeout(() => setTargetSubsectionId(null), 2600);
    return () => window.clearTimeout(timer);
  }, [targetSubsectionId]);

  const handleTabChange = (tab: string) => {
    const tabId = tab as TabId;
    setActiveTab(tabId);
    navigate(`/help?tab=${tabId}`, { replace: true });
  };

  const handleChapterSelect = (chapter: DocChapter) => {
    setActiveChapterId(chapter.id);
    navigate(`/help?tab=docs&chapter=${chapter.id}`, { replace: true });
  };

  const allFaqs = useMemo(
    () => getFaqsFromI18n(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  const filteredFaqs = useMemo(() => {
    const query = faqSearch.trim().toLowerCase();
    if (!query) return allFaqs;
    return allFaqs.filter(
      (faq: FAQItem) => faq.question.toLowerCase().includes(query) || faq.answer.toLowerCase().includes(query)
    );
  }, [faqSearch, allFaqs]);

  useEffect(() => {
    if (expandedFaq && !filteredFaqs.some((faq) => faq.id === expandedFaq)) {
      setExpandedFaq(undefined);
    }
  }, [expandedFaq, filteredFaqs]);

  return (
    <div className="help-page">
      <header className="help-page__header">
        <h1 className="help-page__title">{t('title')}</h1>
        <p className="help-page__lede">{t('lede')}</p>
      </header>

      <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
        <Tabs.List className="list-seg help-page__tabs" aria-label={t('tabsAriaLabel')}>
          <Tabs.Trigger className="list-seg__btn" value="faqs">
            {t('tabs.faqs')}
          </Tabs.Trigger>
          <Tabs.Trigger className="list-seg__btn" value="docs">
            {t('tabs.docs')}
          </Tabs.Trigger>
          <Tabs.Trigger className="list-seg__btn" value="chat">
            {t('tabs.chat')}
          </Tabs.Trigger>
          <Tabs.Trigger className="list-seg__btn" value="contact">
            {t('tabs.contact')}
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="faqs" className="help-page__panel">
          <div className="help-page__faq-toolbar">
            <div className="help-page__search">
              <span className="help-page__search-icon">
                <Search width={16} height={16} />
              </span>
              <input
                type="text"
                className="help-page__search-input"
                placeholder={t('faqs.searchPlaceholder')}
                value={faqSearch}
                onChange={(e) => setFaqSearch(e.target.value)}
                aria-label={t('faqs.searchAriaLabel')}
              />
              {faqSearch && (
                <button
                  type="button"
                  className="help-page__search-clear"
                  onClick={() => setFaqSearch('')}
                  aria-label={t('faqs.clearSearchAriaLabel')}
                >
                  <Close width={14} height={14} />
                </button>
              )}
            </div>
          </div>

          {filteredFaqs.length === 0 ? (
            <p className="help-page__faqs-empty" role="status">
              {t('faqs.empty')}
            </p>
          ) : (
            <Accordion.Root
              type="single"
              collapsible
              value={expandedFaq}
              onValueChange={setExpandedFaq}
              className="help-page__faqs"
            >
              {filteredFaqs.map((faq: FAQItem) => (
                <Accordion.Item key={faq.id} value={faq.id} className="help-page__faq">
                  <Accordion.Header className="help-page__faq-header">
                    <Accordion.Trigger className="help-page__faq-trigger">
                      <CollapsibleChevron className="help-page__faq-chevron" />
                      <span className="help-page__faq-question">{faq.question}</span>
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="help-page__faq-answer">{faq.answer}</Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          )}
        </Tabs.Content>

        <Tabs.Content value="docs" className="help-page__panel">
          <div className="help-page__docs-toolbar">
            <div className="help-page__search">
              <span className="help-page__search-icon">
                <Search width={16} height={16} />
              </span>
              <input
                type="text"
                className="help-page__search-input"
                placeholder={t('docs.searchPlaceholder')}
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
                aria-label={t('docs.searchAriaLabel')}
              />
              {docSearch && (
                <button
                  type="button"
                  className="help-page__search-clear"
                  onClick={() => setDocSearch('')}
                  aria-label={t('docs.clearSearchAriaLabel')}
                >
                  <Close width={14} height={14} />
                </button>
              )}
            </div>
          </div>

          {trimmedDocSearch && filteredDocChapters.length === 0 ? (
            <p className="help-page__faqs-empty" role="status">
              {t('docs.empty')}
            </p>
          ) : (
            <div className="help-page__docs-layout">
              <nav className="help-page__chapter-nav" aria-label={t('docs.chapterNavAriaLabel')}>
                <ul className="help-page__chapter-list">
                  {filteredDocChapters.map((chapter) => {
                    const isActive = chapter.id === activeChapter?.id;
                    return (
                      <li key={chapter.id}>
                        <button
                          type="button"
                          className={`help-page__chapter-btn${isActive ? ' help-page__chapter-btn--active' : ''}`}
                          aria-current={isActive ? 'true' : undefined}
                          onClick={() => handleChapterSelect(chapter)}
                        >
                          <HighlightedText text={chapter.title} query={highlightQuery} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              {activeChapter && (
                <article className="help-page__chapter-panel" aria-labelledby={`help-chapter-${activeChapter.id}`}>
                  <header className="help-page__chapter-header">
                    <h2 className="help-page__chapter-title" id={`help-chapter-${activeChapter.id}`}>
                      <HighlightedText text={activeChapter.title} query={highlightQuery} />
                    </h2>
                    <p className="help-page__chapter-summary">
                      <HighlightedText text={activeChapter.summary} query={highlightQuery} />
                    </p>
                  </header>

                  <div className="help-page__chapter-body">
                    {activeChapter.subsections.map((subsection) => (
                      <section
                        key={subsection.id}
                        id={subsection.id}
                        className={`help-page__doc-section${targetSubsectionId === subsection.id ? ' help-page__doc-section--targeted' : ''}`}
                      >
                        <h3 className="help-page__doc-title">
                          <HighlightedText text={subsection.title} query={highlightQuery} />
                        </h3>
                        <HelpDocContent
                          content={subsection.content}
                          image={subsection.image}
                          highlightQuery={highlightQuery}
                        />
                      </section>
                    ))}
                  </div>
                </article>
              )}
            </div>
          )}
        </Tabs.Content>

        <Tabs.Content value="chat" className="help-page__panel">
          <div className="help-page__placeholder">
            <h2 className="help-page__placeholder-title">{t('chat.title')}</h2>
            <p className="help-page__placeholder-body">{t('chat.body')}</p>
          </div>
        </Tabs.Content>

        <Tabs.Content value="contact" className="help-page__panel">
          <div className="help-page__placeholder">
            <h2 className="help-page__placeholder-title">{t('contact.title')}</h2>
            <p className="help-page__placeholder-body">{t('contact.body')}</p>
            <p className="help-page__contact-email">
              <a href="mailto:support@askewsoft.com" className="help-page__contact-link">
                support@askewsoft.com
              </a>
            </p>
          </div>
        </Tabs.Content>
      </Tabs.Root>

      <footer className="help-page__footer">
        <Link to="/changelog" className="help-page__version-link">
          {t('footer.version', { version })}
        </Link>
      </footer>
    </div>
  );
};

export default HelpScreen;
